/**
 * PDP Inline Options Panel
 * Manages tier selector variant switching + multi-step inline addon panel.
 * The panel replaces product info in the right column (no overlay drawer).
 *
 * Handles both mobile + desktop panels — renders into ALL matching containers.
 */
(function () {
  'use strict';

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function qs(sel, root) { return (root || document).querySelector(sel); }
  function qsa(sel, root) { return Array.from((root || document).querySelectorAll(sel)); }

  /* ── State ── */

  var variants = [];
  var addonSteps = [];
  var selectedVariantId = null;
  var selectedVariantTitle = '';
  var selectedVariantPrice = 0;
  var currentStepIndex = 0;
  var visibleSteps = [];
  var selections = {};
  var savedPsn = '';
  var psnRegex = /^[vV][34] [0-9]{5}$/;

  /* ── Init ── */

  function init() {
    var variantJson = qs('[data-pdp-variant-json]');
    if (variantJson) {
      try { variants = JSON.parse(variantJson.textContent); } catch (e) {
        console.warn('PDP Options: failed to parse variant JSON', e);
      }
    }

    var addonJson = qs('[data-pdp-addon-config]');
    if (addonJson) {
      try { addonSteps = JSON.parse(addonJson.textContent); } catch (e) {
        console.warn('PDP Options: failed to parse addon config', e);
      }
    }

    // Bind close/back/action on ALL panels (mobile + desktop)
    qsa('[data-pdp-panel-close]').forEach(function (el) {
      el.addEventListener('click', closePanel);
    });
    qsa('[data-pdp-panel-back]').forEach(function (el) {
      el.addEventListener('click', goBack);
    });
    qsa('[data-pdp-action-btn]').forEach(function (el) {
      el.addEventListener('click', onActionClick);
    });

    // Set initial variant
    var activeBtn = qs('.pdp-tier__btn--active');
    if (activeBtn) {
      selectedVariantId = activeBtn.getAttribute('data-variant-id');
      selectedVariantTitle = activeBtn.getAttribute('data-variant-title');
      selectedVariantPrice = parseInt(activeBtn.getAttribute('data-variant-price'), 10) || 0;
    } else if (variants.length) {
      selectedVariantId = String(variants[0].id);
      selectedVariantTitle = variants[0].title;
      selectedVariantPrice = variants[0].price;
    }

    bindTierButtons();
    bindBuyButtons();
  }

  /* ── Tier Selector ── */

  function bindTierButtons() {
    qsa('.pdp-tier__btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var variantId = btn.getAttribute('data-variant-id');
        var price = parseInt(btn.getAttribute('data-variant-price'), 10) || 0;
        var sku = btn.getAttribute('data-variant-sku') || '';
        var title = btn.getAttribute('data-variant-title') || '';
        var available = btn.getAttribute('data-variant-available') === 'true';

        selectedVariantId = variantId;
        selectedVariantTitle = title;
        selectedVariantPrice = price;

        qsa('.pdp-tier__btn').forEach(function (b) {
          b.classList.toggle('pdp-tier__btn--active', b.getAttribute('data-variant-id') === variantId);
        });

        qsa('form[action*="/cart/add"] input[name="id"]').forEach(function (input) {
          input.value = variantId;
        });

        qsa('.product-review-system-amount-card-increase h4').forEach(function (el) {
          el.textContent = formatMoney(price);
        });

        qsa('.product-review-system-amount-card-increase p').forEach(function (el) {
          if (sku) {
            el.textContent = 'SKU:' + sku;
            el.style.display = '';
          }
        });

        qsa('button[name="add"]').forEach(function (b) {
          b.disabled = !available;
          b.textContent = available ? 'Buy Now' : 'Sold Out';
        });

        qsa('.product-review-system-amount-logo .color-green').forEach(function (el) {
          el.textContent = formatMoney(Math.round(price / 4));
        });

        qsa('form[action*="/cart/add"] input[name="id"]').forEach(function (input) {
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  }

  /* ── Buy Button ── */

  function bindBuyButtons() {
    qsa('[data-pdp-buy-btn]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        if (addonSteps.length > 0) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          openPanel();
        }
      }, { capture: true });
    });
  }

  /* ── Panel Open/Close ── */

  function openPanel() {
    visibleSteps = addonSteps.filter(function (step) {
      if (!step.tierVisibility || step.tierVisibility === '') return true;
      var tiers = step.tierVisibility.split(',').map(function (t) { return t.trim().toLowerCase(); });
      return tiers.indexOf(selectedVariantTitle.toLowerCase()) !== -1;
    });

    if (visibleSteps.length === 0) {
      submitCart();
      return;
    }

    selections = {};
    savedPsn = '';
    visibleSteps.forEach(function (step, i) {
      if (step.defaultSelected && step.choices && step.choices.length > 0) {
        var firstChoice = step.choices[0];
        selections[i] = {
          variantId: firstChoice.variantId,
          price: firstChoice.price || 0,
          quantity: Math.max(1, parseInt(firstChoice.quantity, 10) || 1)
        };
      } else {
        selections[i] = { variantId: null, price: 0, quantity: 1 };
      }
    });

    currentStepIndex = 0;
    renderStep(currentStepIndex);
    updateSubtotal();

    // Hide product info, show options panel on ALL instances
    qsa('[data-pdp-product-info]').forEach(function (el) { el.classList.add('pdp-hidden'); });
    qsa('[data-pdp-options-panel]').forEach(function (el) {
      el.classList.add('pdp-visible');
      var parent = el.closest('.product-review-text-middle');
      if (parent) parent.classList.add('pdp-panel-open');
    });
  }

  function closePanel() {
    qsa('[data-pdp-product-info]').forEach(function (el) { el.classList.remove('pdp-hidden'); });
    qsa('[data-pdp-options-panel]').forEach(function (el) {
      el.classList.remove('pdp-visible');
      var parent = el.closest('.product-review-text-middle');
      if (parent) parent.classList.remove('pdp-panel-open');
    });
  }

  /* ── Progress Bar ── */

  function updateProgress(index, steps) {
    var total = steps.length;
    if (total <= 1) {
      qsa('[data-pdp-progress]').forEach(function (el) { el.style.display = 'none'; });
      return;
    }
    qsa('[data-pdp-progress]').forEach(function (el) { el.style.display = ''; });

    qsa('[data-pdp-progress-track]').forEach(function (track) {
      track.innerHTML = '';
      for (var i = 0; i < total; i++) {
        var seg = document.createElement('div');
        seg.className = 'pdp-panel__progress-segment';
        if (i < index) seg.className += ' pdp-panel__progress-segment--done';
        else if (i === index) seg.className += ' pdp-panel__progress-segment--active';
        track.appendChild(seg);
      }
    });
  }

  /* ── Step Rendering — renders into ALL panels ── */

  function renderStep(index) {
    currentStepIndex = index;
    var step = visibleSteps[index];
    if (!step) return;

    var isLast = index === visibleSteps.length - 1;

    // Update ALL title elements
    qsa('[data-pdp-panel-title]').forEach(function (el) {
      el.textContent = step.drawerTitle || step.stepLabel || '';
    });

    // Update ALL back buttons
    qsa('[data-pdp-panel-back]').forEach(function (el) {
      el.classList.toggle('pdp-panel__back-btn--hidden', index === 0);
    });

    // Update ALL action buttons
    qsa('[data-pdp-action-btn]').forEach(function (el) {
      el.disabled = false;
      el.textContent = isLast ? 'ADD TO CART' : 'CONTINUE';
    });

    // Update progress bar
    updateProgress(index, visibleSteps);

    // Build step HTML
    var html = buildStepHtml(index, step);

    // Inject into ALL step containers
    qsa('[data-pdp-panel-steps]').forEach(function (container) {
      container.innerHTML = html;
    });

    // Bind option clicks on ALL containers
    qsa('[data-pdp-panel-steps]').forEach(function (container) {
      bindOptionClicks(container);
      // Clear PSN error state on input
      qsa('[data-pdp-psn-input]', container).forEach(function (input) {
        input.addEventListener('input', function () {
          input.classList.remove('pdp-panel__psn-input--error');
          var errorEl = input.parentElement ? qs('[data-pdp-psn-error]', input.parentElement) : null;
          if (errorEl) errorEl.classList.remove('pdp-panel__psn-error--visible');
        });
      });
    });
  }

  function buildStepHtml(index, step) {
    var sel = selections[index];
    var html = '<div class="pdp-panel__step">';

    html += '<div class="pdp-panel__step-group" data-group-index="0">';
    html += '<p class="pdp-panel__step-label">' + (step.stepLabel || '') + '</p>';
    if (step.subtitle) {
      html += '<p class="pdp-panel__step-subtitle">' + step.subtitle + '</p>';
    }

    // Build skip button HTML
    var skipHtml = '';
    if (step.skipLabel) {
      var isSkipSelected = sel && sel.variantId === null;
      skipHtml = '<button type="button" class="pdp-panel__option' +
        (isSkipSelected ? ' pdp-panel__option--selected' : '') +
        '" data-option-action="skip" data-step="' + index + '">' +
        '<span class="pdp-panel__option-label">' + step.skipLabel + '</span>' +
        '<span class="pdp-panel__option-price">+$0.00</span>' +
        '</button>';
    }

    // Build addon choices HTML
    var choicesHtml = '';
    if (step.choices) {
      step.choices.forEach(function (choice, ci) {
        // Skip phantom choices: missing variant, missing label, or both
        if (!choice || !choice.variantId || !choice.label) return;
        var choiceSelected = sel && choice.variantId != null && sel.variantId === choice.variantId;
        var choiceQty = Math.max(1, parseInt(choice.quantity, 10) || 1);
        var lineTotal = (choice.price || 0) * choiceQty;
        choicesHtml += '<button type="button" class="pdp-panel__option' +
          (choiceSelected ? ' pdp-panel__option--selected' : '') +
          '" data-option-action="select" data-step="' + index +
          '" data-choice="' + ci + '" data-variant-id="' + (choice.variantId || '') +
          '" data-price="' + (choice.price || 0) +
          '" data-quantity="' + choiceQty + '">' +
          '<span class="pdp-panel__option-label">' + (choice.label || '') + '</span>' +
          '<span class="pdp-panel__option-price">+' + formatMoney(lineTotal) + '</span>' +
          '</button>';
      });
    }

    // Order: if defaultSelected=true, addon first then skip (POWERlink style)
    //        if defaultSelected=false, skip first then addons (transmission/turbo style)
    if (step.defaultSelected) {
      html += choicesHtml + skipHtml;
    } else {
      html += skipHtml + choicesHtml;
    }

    // PSN field
    if (step.showPsn) {
      var psnVisible = sel && sel.variantId === null;
      html += '<div class="pdp-panel__psn' + (psnVisible ? ' pdp-panel__psn--visible' : '') +
        '" data-pdp-psn-wrap>' +
        '<span class="pdp-panel__psn-label">POWERlink serial number (e.g. V4 54321)</span>' +
        '<input type="text" class="pdp-panel__psn-input" data-pdp-psn-input placeholder="Enter your POWERlink serial number" maxlength="8" autocomplete="off" value="' + (savedPsn || '') + '">' +
        '<div class="pdp-panel__psn-error" data-pdp-psn-error>Please enter a valid serial number (e.g. V4 54321)</div>' +
        '</div>';
    }

    html += '</div></div>';
    return html;
  }

  /* ── Option Click Handlers ── */

  function bindOptionClicks(container) {
    qsa('.pdp-panel__option', container).forEach(function (btn) {
      btn.addEventListener('click', function () {
        var stepIdx = parseInt(btn.getAttribute('data-step'), 10);
        var action = btn.getAttribute('data-option-action');

        if (action === 'skip') {
          selections[stepIdx] = { variantId: null, price: 0, quantity: 1 };
        } else {
          var variantId = btn.getAttribute('data-variant-id');
          var price = parseInt(btn.getAttribute('data-price'), 10) || 0;
          var quantity = Math.max(1, parseInt(btn.getAttribute('data-quantity'), 10) || 1);
          selections[stepIdx] = { variantId: variantId, price: price, quantity: quantity };
        }

        // Update active states in ALL containers
        qsa('[data-pdp-panel-steps] .pdp-panel__option').forEach(function (o) {
          o.classList.remove('pdp-panel__option--selected');
        });
        // Re-select the matching option in ALL containers
        var selector = action === 'skip'
          ? '[data-option-action="skip"][data-step="' + stepIdx + '"]'
          : '[data-option-action="select"][data-step="' + stepIdx + '"][data-choice="' + btn.getAttribute('data-choice') + '"]';
        qsa('[data-pdp-panel-steps] ' + selector).forEach(function (o) {
          o.classList.add('pdp-panel__option--selected');
        });

        // Toggle PSN visibility in ALL containers
        qsa('[data-pdp-psn-wrap]').forEach(function (wrap) {
          wrap.classList.toggle('pdp-panel__psn--visible', action === 'skip');
        });

        updateSubtotal();
      });
    });
  }

  /* ── Subtotal ── */

  function updateSubtotal() {
    var base = selectedVariantPrice;
    var extras = 0;

    Object.keys(selections).forEach(function (key) {
      var sel = selections[key];
      if (sel && sel.price) extras += sel.price * (sel.quantity || 1);
    });

    qsa('[data-pdp-base-price]').forEach(function (el) { el.textContent = formatMoney(base); });
    qsa('[data-pdp-extras-price]').forEach(function (el) { el.textContent = formatMoney(extras); });
    qsa('[data-pdp-extras-row]').forEach(function (el) { el.style.display = extras > 0 ? '' : 'none'; });
    qsa('[data-pdp-subtotal]').forEach(function (el) { el.textContent = formatMoney(base + extras); });
  }

  /* ── Navigation ── */

  function savePsnFromDom() {
    var psnInputs = qsa('[data-pdp-psn-input]');
    for (var i = 0; i < psnInputs.length; i++) {
      if (psnInputs[i].offsetParent !== null) {
        savedPsn = psnInputs[i].value.trim();
        return;
      }
    }
  }

  function onActionClick() {
    if (!validateCurrentStep()) return;
    savePsnFromDom();
    if (currentStepIndex === visibleSteps.length - 1) {
      submitCart();
    } else {
      renderStep(currentStepIndex + 1);
    }
  }

  function goBack() {
    savePsnFromDom();
    if (currentStepIndex > 0) renderStep(currentStepIndex - 1);
  }

  function validateCurrentStep() {
    var step = visibleSteps[currentStepIndex];
    if (!step) return true;

    // PSN is only required when:
    // 1. This step has showPsn enabled
    // 2. The user chose the skip/decline option (no addon selected = "I own one already")
    var sel = selections[currentStepIndex];
    var needsPsn = step.showPsn && sel && sel.variantId === null;

    if (!needsPsn) return true;

    // Find a visible PSN input to validate
    var psnInputs = qsa('[data-pdp-psn-input]');
    for (var i = 0; i < psnInputs.length; i++) {
      var input = psnInputs[i];
      // Only validate the one that's actually visible on screen
      if (input.offsetParent === null) continue;

      var val = input.value.trim();
      var errorEl = input.parentElement ? qs('[data-pdp-psn-error]', input.parentElement) : null;

      if (!psnRegex.test(val)) {
        if (errorEl) errorEl.classList.add('pdp-panel__psn-error--visible');
        // Add error styling to the input — remove and re-add to retrigger shake animation
        input.classList.remove('pdp-panel__psn-input--error');
        void input.offsetWidth;
        input.classList.add('pdp-panel__psn-input--error');
        input.focus();
        return false;
      }
      if (errorEl) errorEl.classList.remove('pdp-panel__psn-error--visible');
      input.classList.remove('pdp-panel__psn-input--error');
      return true;
    }

    return true;
  }

  /* ── Cart Submission ── */

  function submitCart() {
    var items = [];
    var addons = [];

    Object.keys(selections).forEach(function (key) {
      var sel = selections[key];
      if (sel && sel.variantId) {
        addons.push({
          variantId: String(sel.variantId),
          quantity: Math.max(1, parseInt(sel.quantity, 10) || 1)
        });
      }
    });

    var bundleId = addons.length
      ? 'plbundle_' + Date.now() + '_' + Math.random().toString(16).slice(2)
      : null;

    var mainProps = {};
    if (bundleId) {
      mainProps._bundle_id = bundleId;
      mainProps._bundle_role = 'main';
    }

    // Add saved PSN if user chose "I own one already"
    if (savedPsn) {
      mainProps.PSN = savedPsn;
    }

    var mainItem = { id: String(selectedVariantId), quantity: 1 };
    if (Object.keys(mainProps).length) mainItem.properties = mainProps;
    items.push(mainItem);

    addons.forEach(function (addon) {
      var addonProps = {
        _bundle_id: bundleId,
        _bundle_role: 'addon',
        _bundle_parent: String(selectedVariantId)
      };
      if (savedPsn) addonProps.PSN = savedPsn;
      items.push({
        id: addon.variantId,
        quantity: addon.quantity,
        properties: addonProps
      });
    });

    for (var j = 1; j < items.length; j++) {
      if (String(items[j].id) === String(items[0].id)) {
        items[0].quantity += items[j].quantity;
        items.splice(j, 1);
        j--;
      }
    }

    qsa('[data-pdp-action-btn]').forEach(function (el) {
      el.disabled = true;
      el.textContent = 'ADDING...';
    });

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (response) {
        if (!response.ok) {
          return response.json().then(function (data) {
            throw new Error(data.description || 'Failed to add to cart');
          });
        }
        return fetch('/cart.js', { credentials: 'same-origin' })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart }, bubbles: true }));
            document.dispatchEvent(new CustomEvent('ajaxCart:afterCartLoad', { detail: { cart: cart }, bubbles: true }));
            document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { cart: cart }, bubbles: true }));
            if (window.forceImpulseDrawerRefresh) window.forceImpulseDrawerRefresh();
          });
      })
      .then(function () {
        closePanel();
      })
      .catch(function (err) {
        console.error('PDP Options Panel: cart add error', err);
        qsa('[data-pdp-action-btn]').forEach(function (el) {
          el.disabled = false;
          el.textContent = 'ADD TO CART';
        });
      });
  }

  /* ── Boot ── */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
